import type { MapleEngine } from "../../../../../../packages/engine-core/src";
import type { Scene } from "three";
import type { LevelDefinition } from "../levels/types";

export type CortexEventPayload = Record<string, unknown>;

export interface CortexEvent {
  type: string;
  payload?: CortexEventPayload;
}

export type EventHandler = (event: CortexEvent) => void;

export class EventBus {
  private listeners = new Map<string, Set<EventHandler>>();

  on(type: string, handler: EventHandler) {
    const handlers = this.listeners.get(type) ?? new Set<EventHandler>();
    handlers.add(handler);
    this.listeners.set(type, handlers);

    return () => {
      handlers.delete(handler);
      if (!handlers.size) {
        this.listeners.delete(type);
      }
    };
  }

  emit(type: string, payload?: CortexEventPayload) {
    const event = { type, payload } satisfies CortexEvent;
    const handlers = this.listeners.get(type);
    handlers?.forEach((handler) => handler(event));
    this.listeners.get("*")?.forEach((handler) => handler(event));
  }
}

export type ObjectiveStatus = "pending" | "primary" | "completed" | "failed";

export interface Objective {
  id: string;
  text: string;
  status: ObjectiveStatus;
}

export class ObjectivesManager {
  private objectives = new Map<string, Objective>();
  private listeners = new Set<(objectives: Objective[]) => void>();

  addObjective(id: string, text: string, status: ObjectiveStatus = "pending") {
    this.objectives.set(id, { id, text, status });
    this.notify();
  }

  complete(id: string) {
    const objective = this.objectives.get(id);
    if (!objective) return;
    objective.status = "completed";
    this.notify();
  }

  fail(id: string) {
    const objective = this.objectives.get(id);
    if (!objective) return;
    objective.status = "failed";
    this.notify();
  }

  setPrimary(id: string) {
    for (const objective of this.objectives.values()) {
      if (objective.status === "completed" || objective.status === "failed") {
        continue;
      }
      objective.status = objective.id === id ? "primary" : "pending";
    }
    this.notify();
  }

  getAll() {
    return Array.from(this.objectives.values());
  }

  subscribe(listener: (objectives: Objective[]) => void) {
    this.listeners.add(listener);
    listener(this.getAll());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify() {
    const snapshot = this.getAll();
    this.listeners.forEach((listener) => listener(snapshot));
  }
}

export interface MissionContext {
  engine: MapleEngine;
  scene: Scene;
  level: LevelDefinition;
  eventBus: EventBus;
  objectives: ObjectivesManager;
  setStatus: (status: string) => void;
  getState: <T>(key: string, fallback: T) => T;
  setState: <T>(key: string, value: T) => void;
  spawnWave: (count: number) => void;
  getEnemiesRemaining: () => number;
}

export interface Mission {
  init?: (context: MissionContext) => void;
  onStart?: (context: MissionContext) => void;
  onUpdate?: (dt: number, context: MissionContext) => void;
  onEvent?: (event: CortexEvent, context: MissionContext) => void;
}

export interface MissionDefinition {
  id: string;
  name: string;
  description: string;
  createMission: () => Mission;
}

export function createMissionStateMachine<TState extends string>(initialState: TState) {
  let currentState = initialState;
  const listeners = new Set<(state: TState) => void>();

  return {
    getState: () => currentState,
    setState: (nextState: TState) => {
      currentState = nextState;
      listeners.forEach((listener) => listener(currentState));
    },
    subscribe: (listener: (state: TState) => void) => {
      listeners.add(listener);
      listener(currentState);
      return () => listeners.delete(listener);
    },
  };
}